// custom-input-select.js
import React from "react"
import PropTypes from "prop-types"
import { TextField, Grid, Typography } from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(2),
  },
  label: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
      marginBottom: theme.spacing(1),
    },
  },
  formControl: {
    width: "100%",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      padding: "5px", // Remove default padding
      minHeight: "40px", // Set a consistent height
    },
    "& .MuiOutlinedInput-input": {
      padding: "10.5px 14px", // Standard padding for input
    },
  },
  asterisk: {
    color: "red",
    marginRight: theme.spacing(0.5),
  },
  clearIcon: {
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
}))

const CustomInputSelect = ({
  label,
  value,
  onChange,
  options,
  hasError = false,
  errorMessage = undefined,
  required = false,
  showClearIcon = true,
  ...props
}) => {
  const classes = useStyles()

  // Handle change event
  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.value : "")
  }

  return (
    <Grid
      container
      spacing={2}
      className={classes.container}
      alignItems="center"
    >
      <Grid item xs={12} sm={3} className={classes.label}>
        {required && <span className={classes.asterisk}>*</span>}
        <Typography variant="body1">{label}</Typography>
      </Grid>
      <Grid item xs={12} sm={9}>
        <Autocomplete
          value={options.find((opt) => opt.value === value) || null}
          onChange={handleChange}
          options={options}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : option && option.label
              ? option.label
              : ""
          }
          disableClearable={!showClearIcon}
          clearOnBlur={showClearIcon}
          clearOnEscape={showClearIcon}
          classes={{
            clearIndicator: classes.clearIcon
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              error={hasError}
              helperText={hasError ? errorMessage : ""}
              className={classes.textField}
            />
          )}
          {...props}
        />
      </Grid>
    </Grid>
  )
}

CustomInputSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  required: PropTypes.bool,
  showClearIcon: PropTypes.bool,
}

CustomInputSelect.defaultProps = {
  hasError: false,
  errorMessage: "",
  required: false,
  showClearIcon: true,
}

export default CustomInputSelect
